import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaLock } from "react-icons/fa";

const baseUrl = import.meta.env.VITE_APP_BASE_URL;
const SHOW_SKILL_GOAL_COMING_SOON = true;

export const SkillGoal = ({ onGoalCreated }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");
  const [reminder, setReminder] = useState("");
  const [suggestedGoals, setSuggestedGoals] = useState({
    Mathematics: [
      {
        title: "Improve Coordinate Geometry",
        description:
          "You are currently at Developing level, Set goal to reach proficient!",
        subject: "Mathematics",
        topic: "Coordinate Geometry",
      },
      {
        title: "Complete the Quadratic Equations Adventure",
        description:
          "You're 70% through this learning path, set a goal to finish it.",
        subject: "Mathematics",
        topic: "Quadratic Equations",
      },
    ],
    Science: [],
    English: [],
    Computer: [],
  });

  const [subjectsAndTopics, setSubjectsAndTopics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [goalCreated, setGoalCreated] = useState(false);

  const getDefaultTestDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  };
  const [testDate, setTestDate] = useState(getDefaultTestDate());

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");

      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      else if (userId) headers["X-User-ID"] = userId;

      try {
        // Remove self-assessment fetch from here. Use props/context for subjects/topics if needed.
        setError(null);
      } catch (err) {
        console.error("Failed to fetch data", err);
        setError("Failed to fetch suggested goals.");
        setSuggestedGoals([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [baseUrl]); // Removed navigate from dependencies as it's not used in the effect

  const handleAddToMyGoals = (goal) => {
    setSelectedGoal(goal);
    setSelectedSubject(goal.subject);
    setSelectedTopic(goal.topic);

    // Set due date to current date + 3 days
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 3);
    setTestDate(dueDate.toISOString().split("T")[0]);

    // Set reminder to "1 Day Before"
    setReminder("1 Day Before");

    setShowModal(true);
  };

  const handleSubjectChange = (e) => {
    const newSubject = e.target.value;
    setSelectedSubject(newSubject);
    setSelectedTopic(subjectsAndTopics[newSubject]?.[0] || "");
  };

  const handleAddGoal = async () => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userId");

    const headers = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    else if (userId) headers["X-User-ID"] = userId;

    const goalData = {
      subject: selectedSubject,
      topic: selectedTopic,
      details: selectedGoal?.title || "",
      testDate,
      reminder,
      isSkillImprovement: true,
      goal_type: "Skill_updation",
    };

    try {
      const response = await axios.post(
        `${baseUrl}/v2/create-goals`,
        goalData,
        { headers }
      );
      console.log("Goal created:", response.data);

      // Clear cached goals data so fresh data is fetched when navigating to My Goals
      window.goalsData = null;

      // Show success state
      setGoalCreated(true);
      setError("");

      // Remove the goal from the suggested goals
      setSuggestedGoals((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((subject) => {
          updated[subject] = updated[subject].filter(
            (goal) => goal.title !== selectedGoal.title
          );
        });
        return updated;
      });

      setShowModal(false);
      setSelectedSubject(Object.keys(subjectsAndTopics)[0] || "");
      setSelectedTopic(
        subjectsAndTopics[Object.keys(subjectsAndTopics)[0]]?.[0] || ""
      );
      setTestDate("");
      setReminder("");
      setSelectedGoal(null);

      // Show success toast and navigate
      setTimeout(() => {
        setGoalCreated(false);
        // Call the callback to navigate back to MyGoals and refresh data
        if (onGoalCreated) {
          onGoalCreated();
        }
      }, 1500); // Show success message for 1.5 seconds before navigating
    } catch (error) {
      console.error("Failed to add goal", error);
    }
  };

  const subjects = selectedSubject
    ? [selectedSubject]
    : Object.keys(subjectsAndTopics);
  const topics = selectedSubject
    ? [selectedTopic]
    : selectedSubject
    ? subjectsAndTopics[selectedSubject]
    : [];

  if (SHOW_SKILL_GOAL_COMING_SOON) {
    return (
      <div className="p-4 sm:p-6">
        <div className="flex min-h-[320px] w-full items-center justify-center rounded-2xl bg-white px-6 py-20 text-center shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <FaLock className="text-3xl md:text-4xl text-[#0D99FF]" />
            <p className="text-3xl md:text-4xl font-semibold text-[#0D99FF]">
              Coming soon
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="p-4 mx-auto">
        <div className="mb-4">
          <h3 className="text-gray-600 mb-6 text-center sm:text-left text-lg font-medium tracking-wide">
            Suggested Goals
          </h3>

          {loading && <p className="text-center">Loading suggestions...</p>}
          {error && <p className="text-red-500 text-center">{error}</p>}
          {!loading && suggestedGoals.length === 0 && (
            <p className="text-gray-500 text-sm text-center">
              No suggested goals at the moment. Keep up the good work!
            </p>
          )}

          {/* Wrap all subjects inside a soft container */}
          <div className="bg-gray-50 rounded-2xl p-4 sm:p-6">
            {Object.entries(suggestedGoals).map(([subject, goals], idx) => (
              <div
                key={subject}
                className={`mb-8 ${idx === 0 ? "mt-2" : "mt-6"}`} // extra gap before first subject
              >
                {/* Subject Header */}
                <div className="flex items-center mb-4">
                  <div className="h-5 w-1.5 bg-[#0D99FF] rounded-full mr-3"></div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    {subject}
                  </h3>
                </div>

                {/* Goal Cards */}
                {goals.length > 0 ? (
                  goals.map((goal, index) => (
                    <div
                      key={index}
                      className="bg-white shadow-md hover:shadow-lg transition-shadow duration-200 border border-gray-100 rounded-2xl p-5 mb-5 flex flex-col sm:flex-row justify-between items-start sm:items-center"
                    >
                      <div className="flex-1">
                        <h4 className="text-gray-900 font-semibold text-base mb-1">
                          {goal.title}
                        </h4>
                        <p className="text-gray-500 text-sm">
                          {goal.description}
                        </p>
                      </div>

                      <button
                        onClick={() => handleAddToMyGoals(goal)}
                        className="mt-4 sm:mt-0 bg-gradient-to-r from-[#0D99FF] to-[#8B74F8] text-white px-6 py-2.5 rounded-full text-sm font-medium shadow-[0_4px_12px_rgba(117,97,243,0.4)] hover:shadow-[0_6px_14px_rgba(117,97,243,0.6)] transition-all duration-200"
                      >
                        Add to My Goals
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-400 text-sm italic ml-2">
                    No suggested goals yet.
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Modal */}
          {showModal && (
            <>
              <div className="fixed inset-0 bg-white/30 backdrop-blur-sm z-40"></div>

              <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-md max-h-[90vh] overflow-auto">
                  <h3 className="text-lg font-semibold mb-4 text-center">
                    Add Goal
                  </h3>

                  <label className="block mb-2 text-sm">Subject</label>
                  <select
                    value={selectedSubject}
                    disabled
                    onChange={handleSubjectChange}
                    className="w-full border p-2 rounded mb-4"
                  >
                    {subjects.map((subject) => (
                      <option key={subject} value={subject}>
                        {subject}
                      </option>
                    ))}
                  </select>

                  <label className="block mb-2 text-sm">Topic</label>
                  <select
                    value={selectedTopic}
                    disabled
                    onChange={(e) => setSelectedTopic(e.target.value)}
                    className="w-full border p-2 rounded mb-4"
                  >
                    {topics.map((topic) => (
                      <option key={topic} value={topic}>
                        {topic}
                      </option>
                    ))}
                  </select>

                  <label className="block mb-2 text-sm">Details</label>
                  <input
                    type="text"
                    value={selectedGoal?.title || ""}
                    disabled
                    className="w-full border p-2 rounded mb-4 bg-gray-100"
                  />

                  <label className="block mb-2 text-sm">Due Date</label>
                  <input
                    type="date"
                    value={testDate}
                    onChange={(e) => setTestDate(e.target.value)}
                    className="w-full border p-2 rounded mb-4"
                  />

                  <label className="block mb-2 text-sm">Reminder</label>
                  <select
                    value={reminder}
                    onChange={(e) => setReminder(e.target.value)}
                    className="w-full border p-2 rounded mb-4"
                  >
                    <option value="" disabled>
                      Select Reminder
                    </option>
                    <option value="1 Week Before">1 Week Before</option>
                    <option value="3 Days Before">3 Days Before</option>
                    <option value="1 Day Before">1 Day Before</option>
                    <option value="On the Day">On the Day</option>
                  </select>

                  <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2">
                    <button
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 rounded bg-gray-300 text-gray-700 w-full sm:w-auto"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddGoal}
                      disabled={!testDate || !reminder}
                      className={`px-4 py-2 rounded text-white w-full sm:w-auto ${
                        testDate && reminder
                          ? "bg-[#0D99FF]"
                          : "bg-gray-400 cursor-not-allowed"
                      }`}
                    >
                      Add Goal
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Success Toast */}
      {goalCreated && (
        <div className="fixed top-4 right-4 bg-green-200 text-green-800 py-2 px-4 rounded-md shadow-md z-50">
          Goal added successfully!
          <br />
          Returning to My Goals...
        </div>
      )}

      {/* Error Toast */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-200 text-red-800 py-2 px-4 rounded-md shadow-md z-50">
          {error}
        </div>
      )}
    </div>
  );
};

export default SkillGoal;
